import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, Heart, BookOpen, Users, Sprout, 
  Stethoscope, HandHeart, Building2, Megaphone,
  GraduationCap, Brain
} from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <img 
              src="/uxima_logo.png" 
              alt="Uxima Foundation" 
              className="w-10 h-10 object-contain"
            />
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <img 
            src="/uxima_logo.png" 
            alt="Uxima Foundation Logo" 
            className="w-32 h-32 mx-auto mb-6 object-contain"
          />
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            UXIMA FOUNDATION
          </h1>
          <p className="text-2xl font-semibold text-indigo-600 mb-6">
            Educate | Heal | Empower
          </p>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A registered non-profit organization dedicated to promoting education, healthcare, 
            and sustainable development in underserved communities.
          </p>
        </div>

        {/* Executive Summary */}
        <Card className="mb-8 bg-white/80 backdrop-blur-md rounded-2xl border border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-2xl flex items-center gap-3">
              <Building2 className="w-6 h-6 text-indigo-500" />
              Executive Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="text-slate-600 space-y-4">
            <p>
              <strong>UXIMA FOUNDATION</strong> is a registered non-profit organization based in Silvassa, 
              Dadra & Nagar Haveli. Incorporated on <strong>December 21, 2022</strong>, under Business License 
              No. <strong>U85300DN2022NPL005737</strong>, our mission is to promote education, healthcare, 
              and sustainable development in underserved communities.
            </p>
            <p>
              Through grassroots campaigns and targeted interventions, we strive to make a measurable, 
              positive impact and improve the overall quality of life for those in need.
            </p>
          </CardContent>
        </Card>

        {/* Core Pillars Section */}
        <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
          Core Pillars & Completed Initiatives
        </h2>

        {/* Education & Skill Development */}
        <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-3 text-blue-900">
              <BookOpen className="w-6 h-6 text-blue-600" />
              Education & Skill Development
            </CardTitle>
          </CardHeader>
          <CardContent className="text-slate-700">
            <p className="mb-4">
              We believe education is the foundation of a thriving society. Our initiatives are 
              designed to bridge the gap for underprivileged individuals:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <GraduationCap className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Educational Programs:</strong> Providing direct resources, artifacts, 
                  and learning opportunities to underserved students.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Vocational Training:</strong> Equipping youth and adults with practical 
                  skills to enhance their employability and economic independence.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Sports-Focused Welfare:</strong> Promoting physical discipline, teamwork, 
                  and confidence through community sports events.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <HandHeart className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Peace Initiatives:</strong> Fostering community harmony and conflict 
                  resolution through educational outreach.
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Health & Well-being */}
        <Card className="mb-6 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border border-rose-200">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-3 text-rose-900">
              <Stethoscope className="w-6 h-6 text-rose-600" />
              Health & Well-being
            </CardTitle>
          </CardHeader>
          <CardContent className="text-slate-700">
            <p className="mb-4">
              A healthy community is an empowered community. We have prioritized both physical 
              and mental well-being:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Healthcare Outreach:</strong> Facilitating medical camps, health check-ups, 
                  and fitness initiatives to ensure basic healthcare access.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Brain className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Satsangs for Mental Peace:</strong> Organizing community Satsangs to promote 
                  mental health, spiritual grounding, and emotional support.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <HandHeart className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Animal Advocacy Programs:</strong> Raising awareness and providing direct 
                  support for the welfare and protection of local animals.
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Livelihood & Community Empowerment */}
        <Card className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-3 text-emerald-900">
              <Sprout className="w-6 h-6 text-emerald-600" />
              Livelihood & Community Empowerment
            </CardTitle>
          </CardHeader>
          <CardContent className="text-slate-700">
            <p className="mb-4">
              We aim to move beyond charity by building sustainable systems that uplift entire families:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Users className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Sustainable Livelihood Programs:</strong> Developing programs that support 
                  economic stability and teach self-reliance.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Community Center Management:</strong> Operating a dynamic community hub that 
                  hosts events for all ages, fostering a sense of belonging.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Megaphone className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Grassroots Advocacy:</strong> Running continuous advocacy outreach and 
                  grassroots campaigns to ensure the voices of the marginalized are heard.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Sprout className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Social Responsibility:</strong> Partnering with local stakeholders to 
                  promote long-term sustainability and environmental consciousness.
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Free Career Counseling Initiative */}
        <Card className="mb-8 bg-gradient-to-r from-violet-100 to-purple-100 rounded-2xl border border-violet-300 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading text-2xl flex items-center gap-3 text-violet-900">
              <GraduationCap className="w-7 h-7 text-violet-600" />
              Upcoming Major Initiative: Free Career Counseling
            </CardTitle>
          </CardHeader>
          <CardContent className="text-slate-700">
            <p className="mb-4 text-lg">
              Building upon our successful education and skill development programs, UXIMA Foundation 
              is proud to announce a new, highly anticipated project: <strong>Free Career Counseling 
              for Underprivileged Students</strong>.
            </p>
            
            <div className="bg-white/60 rounded-xl p-6 mb-4">
              <h4 className="font-bold text-violet-900 mb-3 text-lg">Program Overview:</h4>
              <p className="mb-4">
                <strong>The Goal:</strong> To provide clear, actionable, and professional career guidance 
                to students who lack access to mentorship, ensuring their background does not limit their 
                future potential.
              </p>
              
              <h4 className="font-bold text-violet-900 mb-2">Key Features:</h4>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-violet-600 font-bold">•</span>
                  One-on-one sessions with industry experts and educators
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-600 font-bold">•</span>
                  Aptitude and skill mapping to align students with the right vocational or academic paths
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-600 font-bold">•</span>
                  Guidance on scholarships, higher education opportunities, and job placement
                </li>
              </ul>
              
              <p>
                <strong>Target Audience:</strong> All economically disadvantaged students in the Silvassa region.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8 border-t border-slate-200">
          <img 
            src="/uxima_logo.png" 
            alt="Uxima Foundation" 
            className="w-16 h-16 mx-auto mb-4 object-contain opacity-80"
          />
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Uxima Foundation. All rights reserved.
          </p>
          <p className="text-slate-400 text-xs mt-2">
            Business License No. U85300DN2022NPL005737 | Silvassa, Dadra & Nagar Haveli
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
